import React, { useState } from 'react'
import { Loader2, Check } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import FlashBanner from './FlashBanner'
import ConfirmDialog from './ConfirmDialog'

export type IntegrationCardProps = {
  icon: React.ReactNode
  name: string
  description: string
  /** 'active' | 'disconnected' | 'invalid' | 'not_configured' */
  status: string
  /** Email, domain, or workspace name shown when connected */
  connectedDetail?: string
  connecting: boolean
  disconnecting: boolean
  /** Omit when the card reports outcomes some other way (e.g. toasts) */
  flash?: 'connected' | 'error' | null
  /** Optional override for the flash banner text (e.g. a specific OAuth failure reason) */
  flashMessage?: string
  onConnect?: () => void
  onDisconnect: () => Promise<void>
  onClearFlash?: () => void
  connectLabel?: string
  disconnectConfirmTitle?: string
  disconnectConfirmMessage?: string
  children?: React.ReactNode
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  icon,
  name,
  description,
  status,
  connectedDetail,
  connecting,
  disconnecting,
  flash,
  flashMessage,
  onConnect,
  onDisconnect,
  onClearFlash,
  connectLabel,
  disconnectConfirmTitle,
  disconnectConfirmMessage,
  children,
}) => {
  const { currentTheme: t } = useTheme()
  const [showConfirm, setShowConfirm] = useState(false)

  const isConnected = status === 'active'
  const isInvalid = status === 'invalid'
  const isNotConfigured = status === 'not_configured'
  const isBusy = connecting || disconnecting

  return (
    <>
      {flash && onClearFlash && (
        <FlashBanner flash={flash} service={name} message={flashMessage} onDismiss={onClearFlash} />
      )}

      <div
        className="rounded-xl p-5 transition-shadow duration-200"
        style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}`, boxShadow: t.cardShadow }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = t.cardShadowHover }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = t.cardShadow }}
      >
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          {/* Left: icon + name + desc */}
          <div className="flex items-center gap-3 sm:flex-1 min-w-0">
            <div className="relative shrink-0">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: t.hoverBackground, border: `1px solid ${t.border}` }}
              >
                {icon}
              </div>
              {isConnected && (
                <span
                  className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#10B981', border: `2px solid ${t.sectionBackground}` }}
                >
                  <Check size={9} strokeWidth={3} color="#fff" />
                </span>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold" style={{ color: t.text }}>{name}</p>
              <p className="text-xs mt-0.5" style={{ color: t.mutedText }}>{description}</p>
            </div>
          </div>

          {/* Right: action */}
          <div className="flex items-center gap-3 shrink-0">
            {isNotConfigured ? (
              <span
                className="text-xs px-2.5 py-1 rounded-md"
                style={{ backgroundColor: t.hoverBackground, color: t.mutedText, border: `1px solid ${t.border}` }}
              >
                Not configured
              </span>
            ) : isConnected || isInvalid ? (
              <>
                {isInvalid && (
                  <span
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium"
                    style={{ backgroundColor: t.errorBackground, color: t.errorText }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.errorText }} />
                    Error
                  </span>
                )}
                <button
                  onClick={() => setShowConfirm(true)}
                  disabled={isBusy}
                  className="flex items-center justify-center gap-1.5 h-9 w-[184px] px-4 rounded-lg text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'transparent', border: `1px solid ${t.border}`, color: t.text }}
                  onMouseEnter={(e) => {
                    if (isBusy) return
                    e.currentTarget.style.backgroundColor = t.errorBackground
                    e.currentTarget.style.color = t.errorText
                    e.currentTarget.style.borderColor = t.errorText
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                    e.currentTarget.style.color = t.text
                    e.currentTarget.style.borderColor = t.border
                  }}
                >
                  {disconnecting ? <Loader2 size={14} className="animate-spin" /> : null}
                  Disconnect
                </button>
              </>
            ) : onConnect ? (
              <button
                onClick={onConnect}
                disabled={isBusy}
                className="flex items-center justify-center gap-2 h-9 w-[184px] px-4 rounded-lg text-sm font-medium whitespace-nowrap text-white transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: t.accent }}
              >
                {connecting ? <><Loader2 size={14} className="animate-spin" />Connecting…</> : (connectLabel ?? `Connect ${name}`)}
              </button>
            ) : null}
          </div>
        </div>

        {/* Connected detail row */}
        {isConnected && connectedDetail && (
          <div
            className="flex items-center gap-2 mt-4 pt-3"
            style={{ borderTop: `1px solid ${t.borderSecondary}` }}
          >
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: '#10B981' }} />
            <span className="text-xs font-mono truncate" style={{ color: t.mutedText }}>{connectedDetail}</span>
          </div>
        )}

        {children}
      </div>

      {showConfirm && (
        <ConfirmDialog
          title={disconnectConfirmTitle ?? `Disconnect ${name}`}
          message={disconnectConfirmMessage ?? `Remove the ${name} integration? Zeph will stop using it immediately.`}
          confirmLabel={disconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
          loading={disconnecting}
          onConfirm={async () => { await onDisconnect(); setShowConfirm(false) }}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  )
}

export default React.memo(IntegrationCard)
